import React, { memo, useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { createUseStyles, Loader, Button } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import { FiltersProvider } from 'components/Filters/Context';

import {
    getDashboard,
    createWidget,
    updateWidget,
    deleteMultipleWidgets,
} from 'lib/services/forceBI';
import { publish } from 'lib/EventBuser';
import { REFRESH_FORCE_BI_VISUALS } from 'lib/events';

import { downloadMultipleSnapshotsAsZip } from './utils/downloadSnapshots';
import { successToast, errorToast } from 'utils/toast';
import { isEqual } from 'utils/objects';
import { downloadMultipleTablesAsZip } from './utils/downloadTables';

import Header from './components/Header';
import GridContainer from './components/GridContainer';
import Crud from './components/Crud';
import Empty from './components/Empty';

import { BASE_LAYOUT_SPECS, CHARTS_LIST, FORCE_BI_SUBMODULE } from './constants';

import useForceBIFilters from './hooks/useForceBIFilters';
import useForceBIUtils from './hooks/useForceBIUtils';

import styles from './styles';

const useStyles = createUseStyles(styles, 'ForceBI');

const ForceBI = memo(
    ({
        entity, // only for tracking
        id,
        title,
        googleMapsApiKey,
        filtersComponent,
        filtersAddButtonSchema,
        defaultCrudFilters,
        defaultDashboardFilters,
        customDashboardFilters,
        canUseEmail,
        showEmailEditor,
        canEdit: isEditable,
        standardFieldsSchemaMap,
        extraFieldsSchemaMap,
        rangeFields,
        crossFilterIdSeparator,
        formatDate,
        getEntitiesManager,
        constants,
        hashHistory,
        ensureRoute,
        forceBIId,
    }) => {
        const classes = useStyles();

        const { t, logEvent, entities } = useWeb5Context();

        const forceBIUtils = useForceBIUtils({
            standardFieldsSchemaMap,
            extraFieldsSchemaMap,
            rangeFields,
            crossFilterIdSeparator,
            formatDate,
            getEntitiesManager,
        });
        const [isLoading, setIsLoading] = useState(true);
        const [widgets, setWidgets] = useState([]);
        const [isCrudOpen, setIsCrudOpen] = useState(false);
        const [crudData, setCrudData] = useState(null);
        const [selected, setSelected] = useState([]);
        const [isError, setIsError] = useState(false);
        const isFirstRender = useRef(true);
        const snapshotsRef = useRef({});
        const gridUtils = useRef(null);
        const debounceTimeoutRef = useRef(null);
        const [areFiltersSaved, setAreFiltersSaved] = useState(true);
        const [areFiltersChanged, setAreFiltersChanged] = useState(false);
        const errorWidgetsRef = useRef([]);

        // If has Custom Dashboard Filters means that this Dashboard is being consumed from an EntityDetail
        // The user won't be able to make any CRUD actions
        const hasCustomDashboardFilters = useMemo(
            () =>
                (customDashboardFilters && Object.keys(customDashboardFilters).length > 0) || false,
            [customDashboardFilters],
        );

        const defaultCache = useMemo(() => {
            return {
                visibleFilters: [...(defaultDashboardFilters?.visibleFilters || [])],
            };
        }, [defaultDashboardFilters]);

        const afterOnChange = useCallback(
            ({ filters, crossFilters, operators, crossOperators }) => {
                if (debounceTimeoutRef.current) {
                    clearTimeout(debounceTimeoutRef.current);
                }

                logEvent({
                    event: entities.ANALYTICS_REPORT.trueName,
                    functionality: 'filter',
                    submodule: FORCE_BI_SUBMODULE,
                });

                // When changing dashboard we need to resetLoaded visuals
                const { resetWidgets } = gridUtils.current;
                debounceTimeoutRef.current = setTimeout(() => {
                    setAreFiltersSaved(false);
                    setAreFiltersChanged(true);
                    resetWidgets({ filters, crossFilters, operators, crossOperators });
                }, 300);
            },
            [logEvent, entities],
        );

        const afterOnChangeCache = useCallback(({ visibleFilters, oldVisibleFilters }) => {
            const hasVisibleFiltersChanged = !isEqual(visibleFilters, oldVisibleFilters);
            if (hasVisibleFiltersChanged) {
                setAreFiltersSaved(false);
                setAreFiltersChanged(true);
            }
        }, []);

        const forceBIFiltersProps = useForceBIFilters({
            defaultCache,
            isDashboard: true,
            afterOnChange,
            afterOnChangeCache,
            constants,
        });

        const dashboardFilters = useMemo(() => {
            return {
                filters: forceBIFiltersProps.filters,
                crossFilters: forceBIFiltersProps.crossFilters,
                operators: forceBIFiltersProps.operators,
                crossOperators: forceBIFiltersProps.crossOperators,
            };
        }, [
            forceBIFiltersProps.filters,
            forceBIFiltersProps.crossFilters,
            forceBIFiltersProps.operators,
            forceBIFiltersProps.crossOperators,
        ]);

        const initWidget = useCallback(
            ({ id, data, onSuccess, ...dashboardFilters }) => {
                publish(REFRESH_FORCE_BI_VISUALS, {
                    id,
                    data,
                    onSuccess,
                    // customDashboardFilters are for filters that do not exist as a filter in the schema, but backend accepts it
                    // one case would be seeing the dashboard from company or opportunity detail and filter by the current company or opportunity
                    customDashboardFilters: customDashboardFilters || {},
                    ...(!dashboardFilters || {}),
                });
            },
            [customDashboardFilters],
        );

        const canCreate = useMemo(() => {
            if (hasCustomDashboardFilters) return false;
            return isEditable || false;
        }, [isEditable, hasCustomDashboardFilters]);

        const canEdit = useMemo(() => {
            if (hasCustomDashboardFilters) return false;
            return isEditable || false;
        }, [isEditable, hasCustomDashboardFilters]);

        const canDelete = useMemo(() => {
            if (hasCustomDashboardFilters) return false;
            return isEditable || false;
        }, [isEditable, hasCustomDashboardFilters]);

        const canShare = useMemo(() => {
            if (hasCustomDashboardFilters) return false;
            return isEditable || false;
        }, [isEditable, hasCustomDashboardFilters]);

        const canSaveFilters = useMemo(() => {
            if (hasCustomDashboardFilters) return false;
            return isEditable || false;
        }, [isEditable, hasCustomDashboardFilters]);

        const canDrag = useMemo(() => {
            if (hasCustomDashboardFilters) return false;
            return selected.length === 0 && isEditable;
        }, [selected, isEditable, hasCustomDashboardFilters]);

        const canResize = useMemo(() => {
            if (hasCustomDashboardFilters) return false;
            return selected.length === 0 && isEditable;
        }, [selected, isEditable, hasCustomDashboardFilters]);

        const getSnapshot = useCallback(({ id, title, image }) => {
            snapshotsRef.current[id] = { title, image };
        }, []);

        const deleteSnapshot = useCallback((id) => {
            if (snapshotsRef.current?.[id]) {
                delete snapshotsRef.current[id];
            }
        }, []);

        const getSingleSnapshot = useCallback((id) => {
            const snapshot = snapshotsRef.current?.[id] || null;
            if (!snapshot) return null;
            return { data: snapshot.image, name: snapshot.title };
        }, []);

        const getBulkSnapshots = useCallback(
            (snapshots) => {
                return Object.entries(snapshots).reduce((obj, [id, snapshot]) => {
                    if (selected.includes(parseInt(id, 10))) obj[id] = snapshot;
                    return obj;
                }, {});
            },
            [selected],
        );

        const getExistingSnapshots = useCallback(() => {
            const snapshots = selected.reduce((arr, id) => {
                if (snapshotsRef.current[id]) arr.push(id);
                return arr;
            }, []);
            return snapshots;
        }, [selected]);

        const updateErrorWidgets = useMemo(() => {
            return {
                add: (id) => {
                    let newId = parseInt(id, 10);
                    let newErrors = [...errorWidgetsRef.current];
                    if (!newErrors.includes(newId)) {
                        newErrors.push(newId);
                        errorWidgetsRef.current = newErrors;
                        deleteSnapshot(id);
                    }
                },
                remove: (id) => {
                    let newId = parseInt(id, 10);
                    let newErrors = [...errorWidgetsRef.current];
                    if (newErrors.includes(newId)) {
                        newErrors = newErrors.filter((current) => current !== newId);
                        errorWidgetsRef.current = newErrors;
                    }
                },
            };
        }, [deleteSnapshot]);

        const beforeDownloadImages = useCallback(() => {
            return new Promise((resolve, reject) => {
                const intervalTime = 200;
                let elapsedTime = 0;
                let timeout = 6000;

                const widgetsToDownload = widgets.reduce((arr, current) => {
                    const id = parseInt(current.id, 10);
                    if (selected.includes(id) && CHARTS_LIST.includes(current.chartType))
                        arr.push(id);
                    return arr;
                }, []);

                const existingSnapshots = getExistingSnapshots();

                if (isEqual(existingSnapshots.sort(), widgetsToDownload.sort())) resolve();

                gridUtils.current.initHiddenWidgets().then(() => {
                    // Wait until the snapshots are created
                    const interval = setInterval(() => {
                        const existingSnapshots = getExistingSnapshots();

                        const selectedCharts = widgets.reduce((arr, current) => {
                            const id = parseInt(current.id, 10);
                            if (selected.includes(id) && CHARTS_LIST.includes(current.chartType)) {
                                arr.push(id);
                            }
                            return arr;
                        }, []);

                        const errorsChartsToDownload = widgets.reduce((arr, current) => {
                            const id = parseInt(current.id, 10);
                            if (
                                selected.includes(id) &&
                                CHARTS_LIST.includes(current.chartType) &&
                                errorWidgetsRef.current.includes(id)
                            ) {
                                arr.push(id);
                            }
                            return arr;
                        }, []);

                        const selectedExistingSnapshotsAndErrors = [
                            ...existingSnapshots,
                            ...errorsChartsToDownload,
                        ].filter((current) => selectedCharts.includes(current));

                        // Control that all is correct to download.
                        // Considering that the available charts (visible and not visible) are equal also considering the errors
                        if (
                            isEqual(existingSnapshots.sort(), widgetsToDownload.sort()) ||
                            isEqual(
                                selectedExistingSnapshotsAndErrors.sort(),
                                selectedCharts.sort(),
                            )
                        ) {
                            clearInterval(interval);
                            resolve();
                        }

                        elapsedTime += intervalTime;
                        if (elapsedTime >= timeout) {
                            clearInterval(interval);
                            reject('Timeout: Arrays did not reach the same length');
                        }
                    }, intervalTime);
                });
            });
        }, [getExistingSnapshots, widgets, selected]);

        const onDownloadImages = useCallback(() => {
            return beforeDownloadImages().then(() => {
                const snapshotsToDownload = getBulkSnapshots(snapshotsRef.current);

                if (!Object.keys(snapshotsToDownload).length) {
                    errorToast({ t, text: t('label_selected_widgets_empty_no_download') });
                    Promise.resolve();
                }

                downloadMultipleSnapshotsAsZip({
                    snapshots: snapshotsToDownload,
                    withTitle: true,
                    t,
                    dashboardName: title,
                }).then(() => {
                    logEvent({
                        event: entities.ANALYTICS_REPORT.trueName,
                        functionality: 'bulkAction',
                        submodule: FORCE_BI_SUBMODULE,
                    });
                    successToast({ t, text: t('label_images_downloaded') });
                });
            }, [getBulkSnapshots, beforeDownloadImages, title, t, logEvent, entities]);
        }, [beforeDownloadImages, getBulkSnapshots, title, logEvent, entities, t]);

        const onDownloadTables = useCallback(() => {
            const widgetsToDownload = widgets.reduce((arr, current) => {
                const id = parseInt(current.id, 10);
                //Prevent to add empty Excels
                if (selected.includes(id) && !errorWidgetsRef.current.includes(id))
                    arr.push(current);
                return arr;
            }, []);

            return downloadMultipleTablesAsZip({
                widgets: widgetsToDownload,
                dashboardFilters,
                forceBIUtils,
                t,
                dashboardName: title,
            }).then(() => {
                logEvent({
                    event: entities.ANALYTICS_REPORT.trueName,
                    functionality: 'bulkAction',
                    submodule: FORCE_BI_SUBMODULE,
                });
            });
        }, [selected, widgets, dashboardFilters, forceBIUtils, title, t, logEvent, entities]);

        const processWidget = useCallback(
            (data) => {
                const { translateFiltersFromBackend } = forceBIUtils;

                const { filters, crossFilters, operators, crossOperators } =
                    translateFiltersFromBackend({
                        filters: data.filters,
                        operators: data.operators,
                    });

                const newWidget = {
                    title: data.title,
                    id: data.id.toString(),
                    dashboardId: id,
                    ...data,
                    filters,
                    crossFilters,
                    operators,
                    crossOperators,
                };

                return newWidget;
            },
            [id, forceBIUtils],
        );

        const mapData = useCallback(
            (data) => {
                let widgets = [];

                if (data.length) {
                    data.forEach((current) => {
                        const newWidget = processWidget(current);
                        widgets.push(newWidget);
                    });
                }

                return widgets;
            },
            [processWidget],
        );

        const initDashboardFilters = useCallback(
            ({ filters = {}, operators = {}, visibleFilters = [] }) => {
                const { init } = forceBIFiltersProps;
                const { processFiltersToInitDashboard } = forceBIUtils;

                const initProps = processFiltersToInitDashboard({ filters, operators });

                init({
                    filters: initProps.filters,
                    crossFilters: initProps.crossFilters,
                    operators: initProps.operators,
                    crossOperators: initProps.crossOperators,
                    visibleFilters: visibleFilters,
                    quickFilters: visibleFilters,
                });
            },
            [forceBIFiltersProps, forceBIUtils],
        );

        const initDashboard = useCallback(() => {
            getDashboard(id)
                .then((response) => {
                    const { filters, operators, visibleFilters } = response.data;
                    let initObj = {};

                    if (!hasCustomDashboardFilters) {
                        initObj = {
                            filters: filters || {},
                            operators: operators || {},
                            visibleFilters: visibleFilters || [],
                        };
                    }

                    initDashboardFilters(initObj);

                    setIsLoading(false);

                    const widgets = mapData(response.data.widgets);

                    setWidgets(widgets);
                })
                .catch((error) => {
                    setIsLoading(false);
                    setIsError(true);
                });
        }, [id, mapData, initDashboardFilters, hasCustomDashboardFilters]);

        const refreshDashboard = useCallback(() => {
            setIsLoading(true);
            setIsError(false);
            initDashboard();
        }, [initDashboard]);

        useEffect(() => {
            if (isFirstRender?.current) {
                isFirstRender.current = false;
                initDashboard();

                if (!entity) return;

                let event = entities.ANALYTICS_REPORT.trueName;
                const { ANALYTICS_REPORT, DASHBOARD } = entities;

                if (entity.entity === ANALYTICS_REPORT.entity) {
                    event = entities.ANALYTICS_REPORT.trueName;
                } else if (entity.entity === DASHBOARD.entity) {
                    event = entities.DASHBOARD.trueName;
                }

                // If there is no event, means that is being consumed from a company or opportunity detail
                if (event) {
                    logEvent({
                        event,
                        functionality: 'detailView',
                        submodule: FORCE_BI_SUBMODULE,
                    });
                }
            }
        }, [initDashboard, refreshDashboard, entities, entity, logEvent]);

        const onSelect = useCallback(
            (id) => {
                if (!id) return;
                let newSelected = [...selected];
                if (newSelected.includes(id)) {
                    newSelected = newSelected.filter((current) => current !== id);
                } else {
                    newSelected.push(id);
                }

                setSelected(newSelected);
            },
            [selected],
        );

        const onSelectAll = useCallback(() => {
            if (selected.length) {
                setSelected([]);
                return;
            }
            const all = widgets.map((current) => current.id);
            setSelected(all);
        }, [widgets, selected]);

        const onClearSelected = useCallback(
            (ids) => {
                if (ids?.lenght) {
                    let newSelected = selected.filter((current) => !ids.includes(current));
                    setSelected(newSelected);
                } else setSelected([]);
            },
            [selected],
        );

        const onOpenCreate = useCallback(() => {
            setIsCrudOpen(true);
        }, []);

        const onCloseCrud = useCallback(() => {
            setIsCrudOpen(false);
        }, []);

        const onAfterCloseCrud = useCallback(() => {
            if (crudData) setCrudData(null);
        }, [crudData]);

        const onEdit = useCallback(
            (newSelected) => {
                setIsCrudOpen(true);
                const { processDataToInitCrud } = forceBIUtils;
                const newCrudData = processDataToInitCrud(newSelected);
                setCrudData(newCrudData);
            },
            [forceBIUtils],
        );

        const onChangeCrud = useCallback((data) => {
            setCrudData(data);
        }, []);

        const afterDeleteWidget = useCallback(
            (ids) => {
                const newWidgets = widgets.filter((current) => !ids.includes(current.id));
                setWidgets(newWidgets);

                const isDeleteMultiple = ids.length > 1;

                if (isDeleteMultiple) {
                    selected.forEach((current) => deleteSnapshot(current));
                    onClearSelected();
                    return;
                } else {
                    deleteSnapshot(ids[0]);
                    onClearSelected(ids);
                }
                successToast({ t, text: t('label_success_delete') });
            },
            [widgets, onClearSelected, selected, deleteSnapshot, t],
        );

        const onDeleteMultiple = useCallback(() => {
            return new Promise((resolve, reject) => {
                deleteMultipleWidgets(selected)
                    .then(() => {
                        afterDeleteWidget(selected);
                        logEvent({
                            event: entities.ANALYTICS_REPORT.trueName,
                            functionality: 'bulkAction',
                            submodule: FORCE_BI_SUBMODULE,
                        });
                        resolve();
                    })
                    .catch(() => {
                        errorToast({ t, text: t('label_error_delete') });
                        reject();
                    });
            });
        }, [t, selected, afterDeleteWidget, logEvent, entities]);

        const onGridInit = useCallback((utils) => {
            gridUtils.current = utils;
        }, []);

        const calculateNewWidgetPosition = useCallback(() => {
            let newItem;

            if (!widgets.length) {
                newItem = { ...BASE_LAYOUT_SPECS, x: 0, y: 0 };
            } else {
                const { findEmptySpot, getLastRow } = gridUtils.current;
                const { w, h } = BASE_LAYOUT_SPECS;
                const emptySpot = findEmptySpot({ w, h });
                if (emptySpot) {
                    newItem = { ...BASE_LAYOUT_SPECS, ...emptySpot };
                } else {
                    const lastRow = getLastRow();
                    newItem = { ...BASE_LAYOUT_SPECS, x: 0, y: lastRow + 1 };
                }
            }

            return newItem;
        }, [widgets]);

        const onChangeGridWidgets = useCallback(
            ({ modifiedWidgets, widgets }) => {
                setWidgets(widgets);
                modifiedWidgets.map((current) => {
                    const { processDataToSendFromCrud } = forceBIUtils;

                    let finalData = processDataToSendFromCrud({
                        data: current,
                    });

                    return updateWidget(current.id, finalData);
                });
            },
            [forceBIUtils],
        );

        const onConfirmCrud = useCallback(
            ({ data }) => {
                return new Promise((resolve, reject) => {
                    if (data?.id) {
                        updateWidget(data.id, data)
                            .then((response) => {
                                if (response.data) {
                                    const newWidget = processWidget(response.data);
                                    const newWidgets = widgets.map((current) => {
                                        if (
                                            parseInt(current.id, 10) === parseInt(newWidget.id, 10)
                                        ) {
                                            return newWidget;
                                        }
                                        return current;
                                    });

                                    setWidgets(newWidgets);

                                    initWidget({
                                        id: data.id,
                                        data: newWidget,
                                        ...dashboardFilters,
                                    });
                                }
                                setCrudData(null);
                                successToast({ t, text: t('label_success_save') });
                                onCloseCrud();
                                resolve();
                            })
                            .catch(() => {
                                errorToast({ t, text: t('label_error_save') });
                                reject();
                            });
                    } else {
                        const newPosition = calculateNewWidgetPosition();
                        const newData = {
                            dashboardId: id,
                            ...data,
                            position: newPosition,
                        };

                        createWidget(newData)
                            .then((response) => {
                                if (response.data) {
                                    const newWidget = processWidget(response.data);
                                    const newWidgets = [...widgets];
                                    newWidgets.push(newWidget);
                                    setWidgets(newWidgets);
                                }
                                successToast({ t, text: t('label_success_save') });
                                setCrudData(null);
                                onCloseCrud();
                                logEvent({
                                    event: entities.ANALYTICS_REPORT.trueName,
                                    functionality: 'createWidget',
                                    submodule: FORCE_BI_SUBMODULE,
                                });
                                resolve();
                            })
                            .catch(() => {
                                errorToast({ t, text: t('label_error_save') });
                                reject();
                            });
                    }
                });
            },
            [
                calculateNewWidgetPosition,
                id,
                onCloseCrud,
                t,
                widgets,
                processWidget,
                initWidget,
                dashboardFilters,
                logEvent,
                entities,
            ],
        );

        const afterDuplicateWidget = useCallback(
            (data) => {
                if (data) {
                    const newWidget = processWidget(data);
                    const newWidgets = [...widgets];
                    newWidgets.push(newWidget);

                    setWidgets(newWidgets);
                }
            },
            [processWidget, widgets],
        );

        const isEmpty = useMemo(() => {
            if (!widgets?.length) return true;
            return false;
        }, [widgets]);

        const onOpenShare = useCallback(
            (url) => {
                showEmailEditor({
                    content: `${t('label_forcebi_dashboard_share')} <a href=${url}>${url}</a>`,
                    signatureAtEnd: true,
                });
                logEvent({
                    event: entities.ANALYTICS_REPORT.trueName,
                    functionality: 'shareToEmail',
                    submodule: FORCE_BI_SUBMODULE,
                });
            },
            [showEmailEditor, t, logEvent, entities],
        );

        const onOpenBulkShare = useCallback(() => {
            return beforeDownloadImages().then(() => {
                let defaults = {};
                const snapshotsToDownload = getBulkSnapshots(snapshotsRef.current);
                if (Object.keys(snapshotsToDownload).length) {
                    let attachments = Object.entries(snapshotsToDownload).reduce(
                        (arr, [id, data]) => {
                            arr.push({ data: data.image, name: data.title });
                            return arr;
                        },
                        [],
                    );
                    defaults.attachments = attachments;
                }
                logEvent({
                    event: entities.ANALYTICS_REPORT.trueName,
                    functionality: 'bulkAction',
                    submodule: FORCE_BI_SUBMODULE,
                });
                showEmailEditor(defaults);
            });
        }, [showEmailEditor, getBulkSnapshots, beforeDownloadImages, logEvent, entities]);

        const hasWidgets = useMemo(() => {
            return widgets?.length > 0 || false;
        }, [widgets]);

        return (
            <FiltersProvider>
                <div className={classes.root}>
                    <Header
                        dashboardId={id}
                        onOpenCreate={onOpenCreate}
                        entity={forceBIUtils.entity}
                        filtersComponent={filtersComponent}
                        filtersAddButtonSchema={filtersAddButtonSchema}
                        onSelectAll={onSelectAll}
                        widgets={widgets}
                        selected={selected}
                        onDeleteMultiple={onDeleteMultiple}
                        onClearSelected={onClearSelected}
                        onOpenShare={onOpenShare}
                        onOpenBulkShare={onOpenBulkShare}
                        onDownloadImages={onDownloadImages}
                        onDownloadTables={onDownloadTables}
                        canUseEmail={canUseEmail}
                        canShare={canShare}
                        canCreate={canCreate}
                        canDelete={canDelete}
                        canSaveFilters={canSaveFilters}
                        forceBIFiltersProps={forceBIFiltersProps}
                        forceBIUtils={forceBIUtils}
                        hasWidgets={hasWidgets}
                        areFiltersSaved={areFiltersSaved}
                        setAreFiltersSaved={setAreFiltersSaved}
                        areFiltersChanged={areFiltersChanged}
                        isLoading={isLoading}
                        customDashboardFilters={customDashboardFilters}
                        hashHistory={hashHistory}
                        ensureRoute={ensureRoute}
                        forceBIId={forceBIId}
                    />
                    <div className={classes.content}>
                        {isLoading && (
                            <div className={classes.loader}>
                                <Loader size="big" />
                            </div>
                        )}
                        {!isLoading && isEmpty && canCreate && (
                            <Empty type="empty">
                                <Button className={classes.emptyButton} onClick={onOpenCreate}>
                                    {t('label_forcebi_crud_title_placeholder')}
                                </Button>
                            </Empty>
                        )}
                        {!isLoading && isEmpty && !canCreate && <Empty type="emptyNoPermission" />}
                        {!isLoading && isError && (
                            <Empty type="error">
                                <Button className={classes.errorButton} onClick={refreshDashboard}>
                                    {t('action_refresh')}
                                </Button>
                            </Empty>
                        )}
                        {!isLoading && !isEmpty && !isError && (
                            <GridContainer
                                widgets={widgets}
                                onEdit={onEdit}
                                onSelect={onSelect}
                                selected={selected}
                                googleMapsApiKey={googleMapsApiKey}
                                getSnapshot={getSnapshot}
                                getSingleSnapshot={getSingleSnapshot}
                                canUseEmail={canUseEmail}
                                showEmailEditor={showEmailEditor}
                                dashboardFilters={dashboardFilters}
                                forceBIUtils={forceBIUtils}
                                onChangeGridWidgets={onChangeGridWidgets}
                                initGrid={onGridInit}
                                initWidget={initWidget}
                                afterDeleteWidget={afterDeleteWidget}
                                afterDuplicateWidget={afterDuplicateWidget}
                                calculateNewWidgetPosition={calculateNewWidgetPosition}
                                canDrag={canDrag}
                                canResize={canResize}
                                canCreate={canCreate}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                canShare={canShare}
                                customDashboardFilters={customDashboardFilters}
                                updateErrorWidgets={updateErrorWidgets}
                            />
                        )}
                        <Crud
                            isOpen={isCrudOpen}
                            data={crudData}
                            onClose={onCloseCrud}
                            onAfterClose={onAfterCloseCrud}
                            onConfirm={onConfirmCrud}
                            onChange={onChangeCrud}
                            googleMapsApiKey={googleMapsApiKey}
                            filtersComponent={filtersComponent}
                            filtersAddButtonSchema={filtersAddButtonSchema}
                            defaultFilters={defaultCrudFilters}
                            forceBIUtils={forceBIUtils}
                            constants={constants}
                            crossFilterIdSeparator={crossFilterIdSeparator}
                        />
                    </div>
                </div>
            </FiltersProvider>
        );
    },
);

export default ForceBI;
